import { Competition, ApiResponse, UnifiedTournamentDTO } from '../types'
import tournamentsRepository from '../repositories/tournaments.repository'
import type { TournamentOverview } from '../repositories/tournaments.repository'

type CompetitionResponse = ApiResponse<Competition | Competition[]>

class RealCompetitionService {
  private loadedOverviews: Map<number, TournamentOverview> = new Map()
  private clubInfoCache: Map<number, any> = new Map()

  /**
   * Clear all caches - should be called when navigating between competitions
   * to ensure fresh data is loaded
   */
  clearCache(): void {
    this.loadedOverviews.clear()
    // Keep club info cache as it rarely changes
  }

  /**
   * Clear cache for a specific tournament
   */
  clearTournamentCache(tournamentId: number): void {
    this.loadedOverviews.delete(tournamentId)
  }

  /**
   * Get all active competitions across all partner communities
   * Uses the new unified endpoint
   */
  async getActiveCompetitions(): Promise<Competition[]> {
    try {
      const allTournaments = await tournamentsRepository.getAllPartnerTournaments()
      const now = new Date()

      // Filter to active tournaments (endTime > now)
      const activeTournaments = allTournaments.filter(t => {
        const endDate = new Date(t.endTime)
        return endDate > now
      })

      // Convert to Competition format
      return this.convertUnifiedToCompetitions(activeTournaments)
    } catch (error) {
      console.error('Error fetching active competitions:', error)
      return []
    }
  }

  /**
   * Get historical competitions filtered by period
   * Uses the new unified endpoint
   */
  async getHistoricalCompetitions(period: string): Promise<Competition[]> {
    try {
      const allTournaments = await tournamentsRepository.getAllPartnerTournaments()
      const now = new Date()

      // Filter to historical tournaments (endTime <= now)
      const historicalTournaments = allTournaments.filter(t => {
        const endDate = new Date(t.endTime)
        return endDate <= now
      })

      // Filter based on period
      const filteredTournaments = this.filterByPeriod(historicalTournaments, period)
      return this.convertUnifiedToCompetitions(filteredTournaments)
    } catch (error) {
      console.error('Error fetching historical competitions:', error)
      return []
    }
  }

  /**
   * Convert unified tournament DTOs to Competition objects
   */
  private convertUnifiedToCompetitions(tournaments: UnifiedTournamentDTO[]): Competition[] {
    return tournaments.map(t => {
      const now = new Date()
      const startDate = new Date(t.startTime)
      const endDate = new Date(t.endTime)

      // Determine status
      let status: 'begins_in' | 'ends_in' | 'ended' = 'ended'
      if (startDate > now) {
        status = 'begins_in'
      } else if (endDate > now) {
        status = 'ends_in'
      }

      return {
        id: t.tournamentId.toString(),
        name: t.tournamentName,
        participants: t.participantsCount,
        prizes: t.awardsCount.toString(),
        startDate: t.startTime,
        endDate: t.endTime,
        mode: t.accountType.toLowerCase() as 'live' | 'demo' | 'all',
        status,
        clubId: t.clubId,
        clubName: t.clubName
      }
    })
  }

  async getCompetitionDetails(competitionId: string): Promise<Competition | null> {
    try {
      // First check if we already have the overview
      const tournamentId = parseInt(competitionId)
      if (!this.loadedOverviews.has(tournamentId)) {
        const overview = await tournamentsRepository.getTournamentOverview(tournamentId)
        this.loadedOverviews.set(tournamentId, overview)
        // Return the competition with the overview data
        return tournamentAdapterService.convertToCompetition(overview.tournamentSettings.tournament!, overview)
      }
      return null
    } catch (error) {
      console.error('Error fetching competition details:', error)
      return null
    }
  }

  async loadCompetitionOverview(competitionId: string): Promise<void> {
    try {
      const tournamentId = parseInt(competitionId)
      if (!this.loadedOverviews.has(tournamentId)) {
        const overview = await tournamentsRepository.getTournamentOverview(tournamentId)
        this.loadedOverviews.set(tournamentId, overview)
      }
    } catch (error) {
      console.error('Error loading competition overview:', error)
    }
  }

  private filterByPeriod(tournaments: UnifiedTournamentDTO[], period: string): UnifiedTournamentDTO[] {
    const now = new Date()

    // Create a new date for calculations to avoid mutating 'now'
    const calculateDate = (days = 0, months = 0, years = 0) => {
      const date = new Date(now)
      date.setFullYear(date.getFullYear() - years)
      date.setMonth(date.getMonth() - months)
      date.setDate(date.getDate() - days)
      return date
    }

    // Convert dates to UTC midnight for consistent comparison
    const toUTCMidnight = (date: Date) => {
      return Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate()
      )
    }

    let cutoffDate: Date
    switch (period) {
      case 'lastWeek':
        cutoffDate = calculateDate(7) // 7 days ago
        break
      case 'lastMonth':
        cutoffDate = calculateDate(0, 1) // 1 month ago
        break
      case 'lastQuarter':
        cutoffDate = calculateDate(0, 3) // 3 months ago
        break
      case 'lastYear':
        cutoffDate = calculateDate(0, 0, 1) // 1 year ago
        break
      case 'allTime':
        return tournaments // No filtering needed
      default:
        console.warn(`Unknown period: ${period}, returning all tournaments`)
        return tournaments
    }

    const cutoffTime = toUTCMidnight(cutoffDate)

    // Filter and sort tournaments
    return tournaments
      .filter(t => {
        const tournamentDate = toUTCMidnight(new Date(t.endTime))
        return tournamentDate >= cutoffTime
      })
      .sort((a, b) => {
        // Sort by end time in descending order (most recent first)
        const dateA = new Date(a.endTime).getTime()
        const dateB = new Date(b.endTime).getTime()
        return dateB - dateA
      })
  }

  /**
   * Get unique clubs from competitions for filter dropdown
   */
  async getAvailableClubs(): Promise<Array<{id: number, name: string}>> {
    try {
      const allTournaments = await tournamentsRepository.getAllPartnerTournaments()
      const clubMap = new Map<number, string>()

      allTournaments.forEach(t => {
        if (!clubMap.has(t.clubId)) {
          clubMap.set(t.clubId, t.clubName)
        }
      })

      return Array.from(clubMap.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    } catch (error) {
      console.error('Error fetching available clubs:', error)
      return []
    }
  }

  /**
   * Get club logo for displaying in competition card
   */
  async getClubLogo(clubId: number): Promise<string | null> {
    try {
      if (this.clubInfoCache.has(clubId)) {
        return this.clubInfoCache.get(clubId).imageUrl
      }

      const clubInfo = await tournamentsRepository.getClubInfo(clubId)
      this.clubInfoCache.set(clubId, clubInfo)
      return clubInfo.imageUrl
    } catch (error) {
      console.error(`Error fetching club logo for clubId ${clubId}:`, error)
      return null
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    }
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  getDaysRemaining(endDate: string): number {
    const end = new Date(endDate)
    const now = new Date()
    
    // Convert both dates to UTC midnight
    const utcEnd = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
    const utcNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())

    const diff = utcEnd - utcNow
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  getCompetitionStatus(endDate: string): string {
    const daysRemaining = this.getDaysRemaining(endDate)
    if (daysRemaining < 0) {
      return 'Completed'
    } else if (daysRemaining === 0) {
      return 'Ending Today'
    } else if (daysRemaining <= 7) {
      return `${daysRemaining} days left`
    } else {
      return `Ends ${this.formatDate(endDate)}`
    }
  }

  calculateCountdown(targetDate: Date): { days: number; hours: number; minutes: number; seconds: number } {
    // Use UTC timestamps for consistent calculations
    const now = new Date()
    const utcNow = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds()
    )
    
    const utcTarget = Date.UTC(
      targetDate.getUTCFullYear(),
      targetDate.getUTCMonth(),
      targetDate.getUTCDate(),
      targetDate.getUTCHours(),
      targetDate.getUTCMinutes(),
      targetDate.getUTCSeconds()
    )

    const difference = utcTarget - utcNow

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds }
  }
}

export default new RealCompetitionService()